import React, { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import {
  ArrowLeft,
  Save,
  Calendar,
  Bell,
  Users,
  Mail,
  MessageSquare,
  BellRing,
  X,
  Loader2,
  Edit,
  MessageCircle,
} from "lucide-react";
import { Tabs } from "@shared/ui/tabs";
import {
  Workflow,
  UpdateWorkflowTriggerRequest,
  UpdateWorkflowActionsRequest,
  WorkflowAction as ApiWorkflowAction,
  MessageContent,
} from "../../types";
import { useToast } from "@shared/hooks/use-toast";
import {
  useUpdateWorkflowTrigger,
  useUpdateWorkflowActions,
  useActivateWorkflow,
} from "../../hooks";
import { usePageViewTimer } from "@shared/hooks/usePageViewTimer";
import { useNavigate } from "react-router-dom";
import { useTemplates } from "@features/ux/messaging/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";

interface WorkflowBuilderProps {
  workflow: Workflow;
  onSave: (workflow: Workflow) => void;
  onCancel: () => void;
  mode?: "view" | "edit";
}

const convertApiActionToUI = (apiAction: ApiWorkflowAction): any => {
  return {
    id: apiAction.sequenceOrder.toString(),
    channel: apiAction.channelType,
    source: apiAction.messageSource.type === "TEMPLATE" ? "template" : "inline",
    content: apiAction.messageSource.content?.body || "",
    templateId: apiAction.messageSource.templateId || undefined,
    timeDelay: apiAction.delay,
    sequenceOrder: apiAction.sequenceOrder,
    channelType: apiAction.channelType,
    messageSource: apiAction.messageSource,
    channelSpecificConfig: apiAction.channelSpecificConfig,
  };
};

const convertUIActionToApi = (
  uiAction: any,
  fallbackSequenceOrder: number
): ApiWorkflowAction => {
  return {
    sequenceOrder: fallbackSequenceOrder,
    channelType: uiAction.channel || "EMAIL",
    delay: uiAction.timeDelay,
    messageSource: {
      type: uiAction.source === "template" ? "TEMPLATE" : "CUSTOM",
      templateId:
        uiAction.templateId !== undefined && uiAction.templateId !== null
          ? parseInt(uiAction.templateId, 10)
          : null,
      content:
        uiAction.source === "inline"
          ? {
              subject: uiAction.subject || "",
              body: uiAction.content || "",
            }
          : null,
    },
    channelSpecificConfig: uiAction.channelSpecificConfig || {
      cc: [],
      bcc: [],
    },
    channel: "EMAIL",
    source: "template",
    subject: "",
  };
};

// Enhanced WorkflowActionViewer with template content
const EnhancedWorkflowActionViewer: React.FC<{
  actions: any[];
  getTemplate: (id: string | number) => any;
}> = ({ actions, getTemplate }) => {
  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case "EMAIL":
        return <Mail className="h-4 w-4" />;
      case "TEXT":
        return <MessageSquare className="h-4 w-4" />;
      case "WHATSAPP":
        return <MessageSquare className="h-4 w-4" />;
      default:
        return <MessageCircle className="h-4 w-4" />;
    }
  };

  const getChannelBadgeColor = (channel: string) => {
    switch (channel) {
      case "EMAIL":
        return "bg-blue-100 text-blue-800";
      case "TEXT":
        return "bg-green-100 text-green-800";
      case "WHATSAPP":
        return "bg-emerald-100 text-emerald-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatTimeDelay = (timeDelay: any) => {
    if (!timeDelay || timeDelay.value === null || timeDelay.value === undefined)
      return "Immediate";
    const { value, unit } = timeDelay;
    return `${value} ${unit}`;
  };

  if (!actions || actions.length === 0) {
    return (
      <div className="text-center p-8 border border-dashed rounded-lg">
        <MessageCircle className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
        <h3 className="text-lg font-medium mb-1">No Actions Configured</h3>
        <p className="text-muted-foreground">
          No workflow actions have been set up yet.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {actions.map((action, index) => {
        const template = action.templateId
          ? getTemplate(action.templateId)
          : null;

        return (
          <Card key={action.id || index} className="relative">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm mr-3 font-medium">
                    {action.sequenceOrder || index + 1}
                  </span>
                  <div className="flex items-center gap-2">
                    {getChannelIcon(action.channel)}
                    <span className="font-medium">
                      {action.channel.charAt(0) +
                        action.channel.slice(1).toLowerCase()}{" "}
                      Message
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${getChannelBadgeColor(
                      action.channel
                    )}`}
                  >
                    {action.channel}
                  </span>
                  {index > 0 && (
                    <div className="flex items-center text-sm text-muted-foreground">
                      <Calendar className="h-3 w-3 mr-1" />
                      {formatTimeDelay(action.timeDelay)}
                    </div>
                  )}
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-4">
              {/* Message Source Type */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Source:</span>
                <span
                  className={`px-2 py-1 rounded text-xs font-medium ${
                    action.source === "template"
                      ? "bg-purple-100 text-purple-800"
                      : "bg-orange-100 text-orange-800"
                  }`}
                >
                  {action.source === "template" ? "Template" : "Custom Message"}
                </span>
              </div>

              {/* ENHANCED: Show template content if it's a template */}
              {action.source === "template" && action.templateId && template ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      Template:
                    </span>
                    <span className="text-sm font-medium">
                      {template.templateName}
                    </span>
                    <span className="text-xs bg-gray-100 px-2 py-1 rounded font-mono">
                      ID: {action.templateId}
                    </span>
                  </div>

                  {/* Show actual template content */}
                  {template.content && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="space-y-3">
                        {/* Subject for email templates */}
                        {action.channel === "EMAIL" &&
                          template.content.subject && (
                            <div>
                              <span className="text-xs font-medium text-blue-700 uppercase tracking-wide">
                                Subject
                              </span>
                              <div className="text-sm font-semibold text-blue-900 mt-1">
                                {template.content.subject}
                              </div>
                            </div>
                          )}

                        {/* Message body */}
                        <div>
                          <span className="text-xs font-medium text-blue-700 uppercase tracking-wide">
                            Content
                          </span>
                          <div className="text-sm text-blue-800 mt-1 whitespace-pre-wrap">
                            {template.content.body}
                          </div>
                        </div>

                        {/* Character count */}
                        <div className="text-xs text-blue-600">
                          Character count: {template.content.body?.length || 0}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : action.source === "template" &&
                action.templateId &&
                !template ? (
                /* Template not loaded yet */
                <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                  <div className="text-sm text-yellow-800">
                    ⚠️ Template content loading... (ID: {action.templateId})
                  </div>
                </div>
              ) : (
                /* Custom message content */
                <div className="space-y-3">
                  {action.messageSource?.content?.subject && (
                    <div className="space-y-1">
                      <span className="text-sm font-medium text-muted-foreground">
                        Subject:
                      </span>
                      <div className="bg-gray-50 p-3 rounded border">
                        <p className="text-sm">
                          {action.messageSource.content.subject}
                        </p>
                      </div>
                    </div>
                  )}

                  {action.messageSource?.content?.body && (
                    <div className="space-y-1">
                      <span className="text-sm font-medium text-muted-foreground">
                        Message Content:
                      </span>
                      <div className="bg-gray-50 p-3 rounded border">
                        <p className="text-sm whitespace-pre-wrap">
                          {action.messageSource.content.body}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Fallback for inline content (legacy support) */}
                  {action.content && !action.messageSource?.content?.body && (
                    <div className="space-y-1">
                      <span className="text-sm font-medium text-muted-foreground">
                        Message Content:
                      </span>
                      <div className="bg-gray-50 p-3 rounded border">
                        <p className="text-sm whitespace-pre-wrap">
                          {action.content}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Channel Specific Config */}
              {action.channelSpecificConfig && (
                <div className="space-y-2">
                  {action.channelSpecificConfig.cc &&
                    action.channelSpecificConfig.cc.length > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">
                          CC:
                        </span>
                        <span className="text-sm">
                          {action.channelSpecificConfig.cc.join(", ")}
                        </span>
                      </div>
                    )}
                  {action.channelSpecificConfig.bcc &&
                    action.channelSpecificConfig.bcc.length > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">
                          BCC:
                        </span>
                        <span className="text-sm">
                          {action.channelSpecificConfig.bcc.join(", ")}
                        </span>
                      </div>
                    )}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {/* Summary */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <MessageCircle className="h-5 w-5 text-blue-600" />
          <span className="font-medium text-blue-900">Workflow Summary</span>
        </div>
        <div className="text-sm text-blue-800">
          Total actions: <span className="font-medium">{actions.length}</span>
        </div>
      </div>
    </div>
  );
};

export const Details: React.FC<WorkflowBuilderProps> = ({
  workflow,
  onSave,
  onCancel,
  mode = "edit",
}) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const isViewMode = mode === "view";

  const [triggerType, setTriggerType] = useState<"time-based" | "event-based">(
    workflow.triggerType === 1 ? "time-based" : "event-based"
  );
  const [actions, setActions] = useState<any[]>(
    workflow.actions?.map(convertApiActionToUI) || []
  );

  const [isSaving, setIsSaving] = useState(false);
  const remoteUtilityId = useRemoteUtilityId();

  const updateTriggerMutation = useUpdateWorkflowTrigger();
  const updateWorkflowActions = useUpdateWorkflowActions();
  const activateWorkflow = useActivateWorkflow();

  // Fetch templates for all channels
  const { data: emailTemplates } = useTemplates({
    channelType: "EMAIL",
    remoteUtilityId,
  });

  const { data: smsTemplates } = useTemplates({
    channelType: "SMS",
    remoteUtilityId,
  });

  const { data: whatsappTemplates } = useTemplates({
    channelType: "WHATSAPP",
    remoteUtilityId,
  });

  // ENHANCED: Create template mapping with full objects
  const templateMap = useMemo(() => {
    const map: Record<string, any> = {};

    // Add email templates
    if (emailTemplates) {
      emailTemplates.forEach((template: any) => {
        map[template.id] = template; // Store full template object
      });
    }

    // Add SMS templates
    if (smsTemplates) {
      smsTemplates.forEach((template: any) => {
        map[template.id] = template;
      });
    }

    // Add WhatsApp templates
    if (whatsappTemplates) {
      whatsappTemplates.forEach((template: any) => {
        map[template.id] = template;
      });
    }

    return map;
  }, [emailTemplates, smsTemplates, whatsappTemplates]);

  // Helper functions
  const getTemplate = (templateId: string | number) => {
    return templateMap[templateId];
  };

  const getTemplateName = (templateId: string | number) => {
    return templateMap[templateId]?.templateName || `Template ${templateId}`;
  };

  const getTemplateContent = (templateId: string | number) => {
    return templateMap[templateId]?.content;
  };

  const handleSaveTrigger = async () => {
    if (isViewMode) return;

    setIsSaving(true);
    try {
      const triggerData: UpdateWorkflowTriggerRequest = {
        triggerType: triggerType === "time-based" ? 1 : 0,
        ...(triggerType === "time-based" && {
          start_date: new Date().toISOString(),
        }),
        ...(triggerType === "event-based" && {
          eventType: "single bill_approve",
        }),
      };

      await updateTriggerMutation.mutateAsync({
        workflowId: workflow.id,
        triggerData: triggerData,
      });
      toast({
        title: "Trigger updated",
        description: "Workflow trigger has been updated successfully.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update workflow trigger.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveActions = async () => {
    if (isViewMode) return;

    setIsSaving(true);
    try {
      const actionsData: UpdateWorkflowActionsRequest = {
        processActions: true,
        actions: actions.map((action, index) =>
          convertUIActionToApi(action, index + 1)
        ),
      };

      await updateWorkflowActions.mutateAsync({
        workflowId: workflow.id,
        actionsData: actionsData,
      });
      toast({
        title: "Actions updated",
        description: "Workflow actions have been updated successfully.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update workflow actions.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleActivateWorkflow = async () => {
    if (isViewMode) return;

    setIsSaving(true);
    try {
      await activateWorkflow.mutateAsync({
        workflowId: workflow.id,
        isActive: true,
      });
      toast({
        title: "Workflow activated",
        description: "Workflow has been activated successfully.",
      });
      onSave(workflow);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to activate workflow.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const getTriggerTypeDisplay = (workflow: Workflow) => {
    if (workflow.triggerType === 1) {
      return "Time-Based Trigger";
    } else {
      return "Event-Based Trigger";
    }
  };

  const getActionSummary = (actions: any[]) => {
    if (!actions || actions.length === 0) return "No actions configured";

    const channelCounts: Record<string, number> = {};
    actions.forEach((action) => {
      channelCounts[action.channel] = (channelCounts[action.channel] || 0) + 1;
    });

    return Object.entries(channelCounts)
      .map(([channel, count]) => `${count} ${channel}${count > 1 ? "s" : ""}`)
      .join(", ");
  };

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case "EMAIL":
        return <Mail className="h-5 w-5" />;
      case "SMS":
        return <MessageSquare className="h-5 w-5" />;
      case "WHATSAPP":
        return <MessageSquare className="h-5 w-5" />;
      case "NOTIFICATION":
        return <Bell className="h-5 w-5" />;
      default:
        return <MessageSquare className="h-5 w-5" />;
    }
  };

  const MessagePreview = ({ content }) => {
    usePageViewTimer({pageName: 'Workflow Preview'})
    return (
      <div className="bg-white rounded-lg border">
        {/* Header Section */}
        <div className="border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
              <h3 className="text-lg font-semibold text-gray-900">
                Message Preview
              </h3>
            </div>
            <X className="h-4 w-4 text-gray-500" />
          </div>
        </div>

        {/* Content Section */}
        <div className="px-6 py-4 space-y-4">
          {/* Subject Line */}
          <div className="border-l-4 border-gray-200 pl-4">
            <div className="text-sm font-medium text-gray-600 mb-1">
              Subject
            </div>
            <div className="text-lg font-semibold text-gray-900">
              {content.subject}
            </div>
          </div>

          {/* Message Body */}
          <div className="border-l-4 border-gray-200 pl-4">
            <div className="text-sm font-medium text-gray-600 mb-2">
              Message
            </div>
            <div className="bg-gray-50 rounded border p-4">
              <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                {content.body}
              </div>
            </div>
          </div>

          {/* Footer Stats */}
          <div className="flex justify-between items-center pt-2 border-t border-gray-100">
            <div className="text-xs text-gray-500">
              Character count: {content.body?.length || 0}
            </div>
            <div className="text-xs text-gray-500">Ready to send</div>
          </div>
        </div>
      </div>
    );
  };

  const tabComponents = {
    details: {
      label: "Details",
      component: (
        <div className="space-y-4 py-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Workflow Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-6">
                <div className="space-y-2">
                  <h3 className="font-medium">General Information</h3>
                  <div className="rounded-md border p-4 bg-muted/30">
                    <dl className="divide-y divide-border">
                      <div className="grid grid-cols-3 py-3 first:pt-0 last:pb-0">
                        <dt className="text-sm font-medium text-muted-foreground">
                          Name
                        </dt>
                        <dd className="col-span-2 text-sm">{workflow.name}</dd>
                      </div>
                      <div className="grid grid-cols-3 py-3 first:pt-0 last:pb-0">
                        <dt className="text-sm font-medium text-muted-foreground">
                          Description
                        </dt>
                        <dd className="col-span-2 text-sm">
                          {workflow.description}
                        </dd>
                      </div>
                      <div className="grid grid-cols-3 py-3 first:pt-0 last:pb-0">
                        <dt className="text-sm font-medium text-muted-foreground">
                          Status
                        </dt>
                        <dd className="col-span-2 text-sm">
                          {workflow.isDraft ? (
                            <Badge variant="outline">Draft</Badge>
                          ) : workflow.isActive ? (
                            <Badge className="bg-success text-success-foreground">
                              Active
                            </Badge>
                          ) : (
                            <Badge className="bg-warning text-warning-foreground">
                              Inactive
                            </Badge>
                          )}
                        </dd>
                      </div>
                      <div className="grid grid-cols-3 py-3 first:pt-0 last:pb-0">
                        <dt className="text-sm font-medium text-muted-foreground">
                          Created
                        </dt>
                        <dd className="col-span-2 text-sm">
                          {workflow.createdDate}
                        </dd>
                      </div>
                      <div className="grid grid-cols-3 py-3 first:pt-0 last:pb-0">
                        <dt className="text-sm font-medium text-muted-foreground">
                          Last Modified
                        </dt>
                        <dd className="col-span-2 text-sm">
                          {workflow.lastModifiedDate}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>

                <div className="space-y-2">
                  <h3 className="font-medium">Trigger Configuration</h3>
                  <div className="rounded-md border p-4 bg-muted/30">
                    <dl className="divide-y divide-border">
                      <div className="grid grid-cols-3 py-3 first:pt-0 last:pb-0">
                        <dt className="text-sm font-medium text-muted-foreground">
                          Trigger Type
                        </dt>
                        <dd className="col-span-2 text-sm">
                          {getTriggerTypeDisplay(workflow)}
                        </dd>
                      </div>
                      {workflow.eventType && (
                        <div className="grid grid-cols-3 py-3 first:pt-0 last:pb-0">
                          <dt className="text-sm font-medium text-muted-foreground">
                            Event Type
                          </dt>
                          <dd className="col-span-2 text-sm">
                            {workflow.eventType}
                          </dd>
                        </div>
                      )}
                      {workflow.startDate && (
                        <div className="grid grid-cols-3 py-3 first:pt-0 last:pb-0">
                          <dt className="text-sm font-medium text-muted-foreground">
                            Start Date
                          </dt>
                          <dd className="col-span-2 text-sm">
                            {workflow.startDate}
                          </dd>
                        </div>
                      )}
                    </dl>
                  </div>
                </div>

                <div className="space-y-2">
                  <h3 className="font-medium">Audience & Actions</h3>
                  <div className="rounded-md border p-4 bg-muted/30">
                    <dl className="divide-y divide-border">
                      <div className="grid grid-cols-3 py-3 first:pt-0 last:pb-0">
                        <dt className="text-sm font-medium text-muted-foreground">
                          Recipient Type
                        </dt>
                        <dd className="col-span-2 text-sm">
                          {workflow.recipientTypeDisplay}
                        </dd>
                      </div>
                      <div className="grid grid-cols-3 py-3 first:pt-0 last:pb-0">
                        <dt className="text-sm font-medium text-muted-foreground">
                          Actions
                        </dt>
                        <dd className="col-span-2 text-sm">
                          {actions.length} action(s):{" "}
                          {getActionSummary(actions)}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>

                <div className="space-y-2">
                  <h3 className="font-medium">Performance</h3>
                  <div className="rounded-md border p-4 bg-muted/30">
                    <dl className="divide-y divide-border">
                      <div className="grid grid-cols-3 py-3 first:pt-0 last:pb-0">
                        <dt className="text-sm font-medium text-muted-foreground">
                          Total Notifications
                        </dt>
                        <dd className="col-span-2 text-sm">
                          {workflow.totalNotifications}
                        </dd>
                      </div>
                      <div className="grid grid-cols-3 py-3 first:pt-0 last:pb-0">
                        <dt className="text-sm font-medium text-muted-foreground">
                          Delivered
                        </dt>
                        <dd className="col-span-2 text-sm">
                          {workflow.deliveredCount}
                        </dd>
                      </div>
                      <div className="grid grid-cols-3 py-3 first:pt-0 last:pb-0">
                        <dt className="text-sm font-medium text-muted-foreground">
                          Failed
                        </dt>
                        <dd className="col-span-2 text-sm">
                          {workflow.failedCount}
                        </dd>
                      </div>
                      <div className="grid grid-cols-3 py-3 first:pt-0 last:pb-0">
                        <dt className="text-sm font-medium text-muted-foreground">
                          Success Rate
                        </dt>
                        <dd className="col-span-2 text-sm">
                          {workflow.successRate.toFixed(1)}%
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      ),
    },

    actions: {
      label: "Actions",
      component: (
        <div className="space-y-4 py-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Communication Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm mb-4">
                Configure the messages that will be sent in this workflow.
              </p>

              {/* Use enhanced viewer that shows template content */}
              <EnhancedWorkflowActionViewer
                actions={actions}
                getTemplate={getTemplate}
              />
            </CardContent>
          </Card>
        </div>
      ),
    },

    preview: {
      label: "Preview",
      component: (
        <div className="py-4">
          <Card>
            {/* Card Header with better spacing */}
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                  Message Preview
                </CardTitle>
                <div className="text-sm text-muted-foreground">
                  {actions.length}{" "}
                  {actions.length === 1 ? "message" : "messages"}
                </div>
              </div>
            </CardHeader>

            <CardContent>
              {actions.length === 0 ? (
                /* Empty State - Better structured */
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <div className="w-8 h-8 border-2 border-gray-300 border-dashed rounded"></div>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No messages configured
                  </h3>
                  <p className="text-muted-foreground max-w-sm mx-auto">
                    Add some actions to see their preview here. Messages will
                    appear in the order they'll be sent.
                  </p>
                </div>
              ) : (
                /* Messages List - Improved structure */
                <div className="space-y-6">
                  {actions.map((action, index) => (
                    <div key={action.id} className="relative">
                      {/* Message Card */}
                      <div className="border rounded-lg overflow-hidden">
                        {/* Message Header */}
                        <div className="bg-gray-50 px-6 py-3 border-b">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {getChannelIcon(action.channel)}
                              <div>
                                <h4 className="font-medium text-gray-900">
                                  {action.channel}{" "}
                                  {action.source === "template"
                                    ? "Template"
                                    : "Message"}
                                </h4>
                                {action.source === "template" &&
                                  action.templateId && (
                                    <p className="text-sm text-muted-foreground">
                                      {getTemplateName(action.templateId)}
                                    </p>
                                  )}
                              </div>
                            </div>

                            {/* Timing Info */}
                            {action.timeDelay && (
                              <div className="text-right">
                                <div className="text-sm font-medium text-gray-600">
                                  +{action.timeDelay.value}{" "}
                                  {action.timeDelay.unit}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  delay
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Message Content */}
                        <div className="p-6">
                          {action.source === "template" && action.templateId ? (
                            (() => {
                              const templateContent = getTemplateContent(
                                action.templateId
                              );

                              if (templateContent) {
                                return (
                                  <div className="space-y-4">
                                    {/* Subject for email templates */}
                                    {action.channel === "EMAIL" &&
                                      templateContent.subject && (
                                        <div className="border-l-4 border-gray-200 pl-4">
                                          <div className="text-sm font-medium text-gray-600 mb-1">
                                            Subject
                                          </div>
                                          <div className="text-lg font-semibold text-gray-900">
                                            {templateContent.subject}
                                          </div>
                                        </div>
                                      )}

                                    {/* Message body */}
                                    <div className="border-l-4 border-gray-200 pl-4">
                                      <div className="text-sm font-medium text-gray-600 mb-2">
                                        Message
                                      </div>
                                      <div className="bg-gray-50 rounded border p-4">
                                        <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                                          {templateContent.body}
                                        </div>
                                      </div>
                                    </div>

                                    {/* Stats */}
                                    <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                                      <div className="text-xs text-gray-500">
                                        {templateContent.body?.length || 0}{" "}
                                        characters
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        Template content
                                      </div>
                                    </div>
                                  </div>
                                );
                              } else {
                                return (
                                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                                    <div className="flex items-center gap-3">
                                      <div className="w-4 h-4 border-2 border-gray-400 border-dashed rounded-full animate-pulse"></div>
                                      <div>
                                        <div className="text-sm font-medium text-gray-700 mb-1">
                                          Loading template content
                                        </div>
                                        <div className="text-xs text-gray-500">
                                          Template ID: {action.templateId}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              }
                            })()
                          ) : action.source === "inline" && action.content ? (
                            <div className="space-y-4">
                              {/* Subject Line */}
                              <div className="border-l-4 border-gray-200 pl-4">
                                <div className="text-sm font-medium text-gray-600 mb-1">
                                  Subject
                                </div>
                                <div className="text-lg font-semibold text-gray-900">
                                  {action.messageSource.content.subject}
                                </div>
                              </div>

                              {/* Message Body */}
                              <div className="border-l-4 border-gray-200 pl-4">
                                <div className="text-sm font-medium text-gray-600 mb-2">
                                  Message
                                </div>
                                <div className="bg-gray-50 rounded border p-4">
                                  <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                                    {action.messageSource.content.body}
                                  </div>
                                </div>
                              </div>

                              {/* Stats */}
                              <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                                <div className="text-xs text-gray-500">
                                  {action.messageSource.content.body?.length ||
                                    0}{" "}
                                  characters
                                </div>
                                <div className="text-xs text-gray-500">
                                  Inline content
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="text-center py-4 text-muted-foreground">
                              No content available
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Flow Connector */}
                      {index < actions.length - 1 && (
                        <div className="flex items-center justify-center py-4">
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <div className="h-px w-16 bg-gray-300"></div>
                            <div className="bg-white border border-gray-300 rounded-full px-3 py-1">
                              {actions[index + 1].timeDelay?.value}{" "}
                              {actions[index + 1].timeDelay?.unit}
                            </div>
                            <div className="h-px w-16 bg-gray-300"></div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      ),
    },
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onCancel}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h2 className="text-xl font-semibold">
            {isViewMode ? "View" : "Edit"} Workflow: {workflow.name}
          </h2>
        </div>
        <div className="flex gap-2">
          {!isViewMode && (
            <>
              <Button
                variant="outline"
                onClick={handleSaveTrigger}
                disabled={isSaving}
              >
                {isSaving ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Save Trigger
              </Button>
              <Button
                variant="outline"
                onClick={handleSaveActions}
                disabled={isSaving}
              >
                {isSaving ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Save Actions
              </Button>
              {workflow.isDraft && (
                <Button onClick={handleActivateWorkflow} disabled={isSaving}>
                  {isSaving ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Bell className="mr-2 h-4 w-4" />
                  )}
                  Activate Workflow
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      <Tabs
        defaultValue="details"
        tabComponents={tabComponents}
        disableUrlSync={true}
        urlMapping={{
          details: "details",
          actions: "actions",
          preview: "preview",
        }}
      />
    </div>
  );
};
